/**
 * `FlyerDivider` — the drop card in the crate. `docs/design-direction.md` §8.2, §6.11.
 *
 * Every seventh row of the ledger, a full-bleed cream flyer rotated 1.2° with two
 * tape strips, black Display 800 caps, a mono date and a coarse halftone. It breaks
 * the list into the *drops* a channel actually posts, which is how a crate is
 * organised in real life — by the session it came from, not as an infinite scroll of
 * identical rows.
 *
 * **It commits, or it goes dark** (§12.14). A half-hearted paper surface — no
 * rotation, no tape, no hard-cut edge — reads as a rendering bug rather than as a
 * printed object, so the 1.2° rotation, the `clip-path` cut and both tape strips are
 * part of the component rather than options on it. The tape is `texture.css`'s
 * `.paper__tape` recipe and the coarse screen is the `Halftone` primitive.
 *
 * Inversion is the strongest move in the system and is capped at 25% of the viewport
 * (§5.5): a divider is one band inside a scrolling ledger and stays far under that.
 * The Queue sheet is the single documented exception, and it is a different screen.
 *
 * It carries no ARIA role. A flyer is a printed section marker in a virtualised list,
 * and the list's own semantics belong to whoever assembles it (T41) — a `separator`
 * with text inside it, or a heading orphaned from its `<li>`, would both describe the
 * page less accurately than plain content does.
 */

import type { JSX } from 'react';

import styles from './components.module.css';
import { Halftone } from './primitives/Halftone';
import { cx } from './primitives/Rule';

export interface FlyerDividerProps {
  /** The drop's name as the channel posted it, e.g. `ПОДБОРКА #12`. */
  readonly title: string;
  /** A mono date line. Omitted entirely when unknown — never a placeholder (§12.6). */
  readonly date?: string | null | undefined;
  readonly className?: string | undefined;
}

export function FlyerDivider({ title, date = null, className }: FlyerDividerProps): JSX.Element {
  const hasDate = typeof date === 'string' && date.trim() !== '';

  return (
    <Halftone coarse as="div" className={cx(styles.flyer, className)}>
      <span className="paper__tape" aria-hidden="true" />
      <span className={cx('paper__tape', styles.tape2)} aria-hidden="true" />

      <span className={cx('t-display', 't-20', styles.flyerTitle)}>{title}</span>
      {hasDate ? <span className={cx('t-mono', 't-11', styles.flyerDate)}>{date}</span> : null}
    </Halftone>
  );
}
